import { Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  BaseLayerConfig,
  JepBasemap,
  JepBasemapProperties
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { store } from '../../../jmkw.store'; // Add store import
import { PurlyService } from '../../services/purly.service';
import {
  AvailableBasemaps,
  BASEMAP_LAYER_CONFIGS,
  updateBasemapConfigsWithPurlyUrls
} from './basemap.config';

const DEFAULT_BASEMAPS: string[] = ['jmkw-dark-basemap', 'jmkw-light-basemap'];
@Injectable({ providedIn: 'root' })
export class BasemapRegistryService implements OnDestroy {
  private readonly _logger = inject(NGXLogger);
  private readonly _purlyService = inject(PurlyService);
  private readonly _basemapsSubject = new BehaviorSubject<AvailableBasemaps>(
    {} as AvailableBasemaps
  );
  public readonly basemaps$ = this._basemapsSubject.asObservable();

  constructor() {
    this.initializeBasemaps();
  }

  ngOnDestroy(): void {
    this._basemapsSubject.complete();
  }

  getBasemaps(): AvailableBasemaps {
    return this._basemapsSubject.value;
  }

  registerBasemap(basemap: JepBasemap) {
    if (basemap.id) {
      this._basemapsSubject.next({
        ...this._basemapsSubject.value,
        [basemap.id]: basemap
      });
    } else {
      // Handle case where ID is null or undefined
      this._logger.warn('[BasemapRegistryService] Basemap missing ID:', basemap);
    }
  }

  private initializeBasemaps(): void {
    // Wait for both map to be available AND Purly service to be initialized (or fail)
    // This ensures we have signed URLs before creating basemaps
    combineLatest([
      toObservable(store.map).pipe(filter(Boolean)),
      this._purlyService.initializationResult$.pipe(filter(Boolean))
    ])
      .pipe(
        take(1),
        tap(() => this.updateBasemaps())
      )
      .subscribe();
  }

  private urlHasProtocol(config: BaseLayerConfig): boolean {
    return config.url.startsWith('http') || config.url.startsWith('https');
  }

  private getConfigWithDomain(config: BaseLayerConfig): BaseLayerConfig {
    return {
      ...config,
      url: `${store.env.basemapDomain()}${config.url}`
    };
  }

  private updateBasemaps(): void {
    const basemaps = this.createBasemaps();
    this._basemapsSubject.next(basemaps);
  }

  private createBasemaps(): AvailableBasemaps {
    const enabledBasemaps =
      store.env.themeConfig?.enabledBasemaps?.() || DEFAULT_BASEMAPS;
    return enabledBasemaps.length > 0
      ? this.createCustomBasemaps(enabledBasemaps)
      : this.createCustomBasemaps(DEFAULT_BASEMAPS);
  }

  private createCustomBasemaps(enabledBasemaps: string[]): AvailableBasemaps {
    // Get current basemap layer configs, potentially updated with Purly URLs
    const basemapConfigs = this._purlyService.isInitialized
      ? updateBasemapConfigsWithPurlyUrls(this._purlyService.getBasemapUrls())
      : BASEMAP_LAYER_CONFIGS;

    return enabledBasemaps.reduce<AvailableBasemaps>((basemaps, key) => {
      const config = basemapConfigs[key];
      if (!config) return basemaps;

      const configWithDomain = this.urlHasProtocol(config)
        ? config
        : this.getConfigWithDomain(config);

      const layer = this.createLayer(configWithDomain);
      if (!layer) return basemaps;

      basemaps[key] = new JepBasemap({
        baseLayers: [layer],
        title: config.title,
        id: key,
        theme: config.theme
      } as JepBasemapProperties);

      return basemaps;
    }, {} as AvailableBasemaps);
  }

  private createLayer(config: BaseLayerConfig) {
    try {
      // VectorTileLayer automatically detects if the URL points to a style JSON
      // or a VectorTileServer based on the response content-type
      const layer = new config.type({
        url: config.url,
        id: config.layer_id
      });

      return layer;
    } catch (error) {
      this._logger.error(`[BasemapRegistryService] Failed to create layer ${config.layer_id}:`, error);
      return null;
    }
  }
}
